import { supabase } from './supabase';

export function logActivity(orgId, category, action, summary, details = null) {
  if (!orgId) return;
  supabase.from('activity_log').insert({
    org_id: orgId, action_type: action, summary, details: details ? { ...details, category } : { category }
  }).then(({ error }) => { if (error) console.error('Activity log error:', error); });
}

// --- Price update batching ---
// Accumulates price changes within 5 minutes into one log entry
let priceBatch = [];
let priceTimer = null;
let priceBatchOrgId = null;

export function logPriceUpdate(orgId, ingName, oldPrice, newPrice) {
  const pct = oldPrice > 0 ? ((newPrice - oldPrice) / oldPrice * 100) : 0;
  priceBatch.push({ name: ingName, old: oldPrice, new: newPrice, pct });
  priceBatchOrgId = orgId;

  clearTimeout(priceTimer);
  priceTimer = setTimeout(flushPriceBatch, 5000); // flush after 5s of inactivity
}

function flushPriceBatch() {
  if (!priceBatch.length || !priceBatchOrgId) return;
  const items = [...priceBatch];
  const oid = priceBatchOrgId;
  priceBatch = [];
  priceBatchOrgId = null;

  let summary;
  if (items.length === 1) {
    const i = items[0];
    summary = `Updated ${i.name}: $${i.old.toFixed(2)} → $${i.new.toFixed(2)} (${i.pct >= 0 ? '+' : ''}${i.pct.toFixed(1)}%)`;
  } else {
    summary = `Updated ${items.length} ingredient prices`;
  }

  logActivity(oid, 'price', 'price_update', summary, { items, count: items.length });
}

// Flush on page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', flushPriceBatch);
}

// --- Invoice scan ---
export function logInvoiceScan(orgId, vendor, invoiceNumber, total, itemCount, priceChanges) {
  logActivity(orgId, 'invoice', 'invoice_scan',
    `Scanned ${vendor} invoice${invoiceNumber ? ' #' + invoiceNumber : ''} — $${total.toLocaleString(undefined, { minimumFractionDigits: 2 })}, ${itemCount} items${priceChanges > 0 ? ', ' + priceChanges + ' price updates' : ''}`,
    { vendor, invoiceNumber, total, itemCount, priceChanges }
  );
}

// --- Team changes ---
export function logTeamChange(orgId, action, targetEmail, detail) {
  logActivity(orgId, 'team', action, detail, { targetEmail });
}

// --- Menu item changes ---
export function logMenuChange(orgId, action, itemName, detail) {
  logActivity(orgId, 'menu', action, detail || `${action} menu item: ${itemName}`, { itemName });
}

// --- Batch changes ---
export function logBatchChange(orgId, action, batchName, detail) {
  logActivity(orgId, 'batch', action, detail || `${action} batch: ${batchName}`, { batchName });
}

// --- Settings changes ---
export function logSettingsChange(orgId, field, oldVal, newVal) {
  logActivity(orgId, 'settings', 'settings_update',
    `Changed ${field}: ${oldVal} → ${newVal}`,
    { field, oldVal, newVal }
  );
}

// --- Ingredient add/delete/replace ---
export function logIngredientAdd(orgId, name) {
  logActivity(orgId, 'price', 'ingredient_added', `Added ingredient: ${name}`, { name });
}

export function logIngredientDelete(orgId, name) {
  logActivity(orgId, 'price', 'ingredient_deleted', `Deleted ingredient: ${name}`, { name });
}

// --- Purchases & expenses ---
export function logPurchase(orgId, vendor, amount, category) {
  logActivity(orgId, 'invoice', 'purchase_added',
    `Purchase: $${Math.abs(amount).toFixed(2)} from ${vendor} (${category})`,
    { vendor, amount, category }
  );
}

export function logCredit(orgId, vendor, amount, category) {
  logActivity(orgId, 'invoice', 'credit_added',
    `Credit recorded: −$${Math.abs(amount).toFixed(2)} from ${vendor} (${category})`,
    { vendor, amount, category }
  );
}

export function logExpense(orgId, vendor, amount, category) {
  logActivity(orgId, 'invoice', 'expense_added',
    `Expense: $${Math.abs(amount).toFixed(2)} — ${vendor} (${category})`,
    { vendor, amount, category }
  );
}

export function logRefund(orgId, vendor, amount, category) {
  logActivity(orgId, 'invoice', 'refund_added',
    `Refund recorded: −$${Math.abs(amount).toFixed(2)} — ${vendor} (${category})`,
    { vendor, amount, category }
  );
}

export function logEntryEdit(orgId, type, vendor, changes) {
  const parts = changes.map(([field, from, to]) => `${field} ${from} → ${to}`);
  logActivity(orgId, 'invoice', `${type}_edited`,
    `Edited ${vendor} ${type}: ${parts.join(', ')}`,
    { vendor, type, changes }
  );
}
